import React, { useState } from "react";
import { useWorkspaces } from "@/contexts/WorkspaceContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Smartphone, Plus, Pencil, Trash2, Check, AlertTriangle, Sparkles, Loader2 } from "lucide-react";

export default function WorkspacesPage() {
  const { workspaces, active, max, loading, createOne, rename, remove, switchTo } = useWorkspaces();
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submitNew = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await createOne(newName);
      setShowNew(false); setNewName("");
    } catch (err) { setError(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };

  const submitEdit = async () => {
    if (!editingName.trim()) return;
    setBusy(true); setError("");
    try {
      await rename(editing.id, editingName.trim());
      setEditing(null);
    } catch (err) { setError(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };

  const submitDelete = async (w) => {
    if (!window.confirm(`¿Eliminar "${w.name}" y TODOS sus datos? Esta acción no se puede deshacer.`)) return;
    setBusy(true); setError("");
    try {
      await remove(w.id);
    } catch (err) { setError(err.response?.data?.detail || err.message); alert(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const remaining = Math.max(0, (max || 1) - workspaces.length);

  return (
    <div className="p-8 max-w-3xl" data-testid="workspaces-page">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Smartphone className="w-6 h-6 text-emerald-500" /> Mis WhatsApps
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Administrá las instancias de WhatsApp de tu cuenta. Cada una tiene sus propios contactos, comandos, palabras clave, catálogo y campañas — totalmente aisladas.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Estás usando <strong>{workspaces.length}</strong> de <strong>{max}</strong>.
            {remaining > 0 ? ` Te quedan ${remaining} disponibles.` : " Llegaste al límite — pedile a soporte que lo amplíe."}
          </p>
        </div>
        <Button
          onClick={() => setShowNew(true)}
          disabled={remaining <= 0}
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
          data-testid="ws-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Nuevo WhatsApp
        </Button>
      </div>

      {error && (
        <Card className="mb-4 border-red-300 bg-red-50">
          <CardContent className="p-3 flex items-center gap-2 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4" /> {error}
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {workspaces.map((w) => {
          const isActive = active?.id === w.id;
          return (
            <Card key={w.id} className={`transition ${isActive ? "border-emerald-400 ring-2 ring-emerald-100" : ""}`} data-testid={`ws-card-${w.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                  <Smartphone className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate" data-testid={`ws-name-${w.id}`}>{w.name}</p>
                    {isActive && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Activo</span>}
                    {!w.is_active && <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full">Pausado</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Creado {w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  {!isActive && (
                    <Button size="sm" variant="outline" onClick={() => switchTo(w.id)} data-testid={`ws-switch-${w.id}`}>
                      <Check className="w-3 h-3 mr-1" /> Activar
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(w); setEditingName(w.name); }} data-testid={`ws-edit-${w.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => submitDelete(w)}
                    disabled={workspaces.length <= 1}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`ws-delete-${w.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tip card */}
      <Card className="mt-6 border-amber-200 bg-amber-50/50">
        <CardContent className="p-4 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900 space-y-1">
            <p><strong>¿Cómo cambio entre WhatsApps?</strong> Usá el selector arriba del menú lateral. Al cambiar, todos los datos se recargan según el WhatsApp elegido.</p>
            <p><strong>Permisos por WhatsApp:</strong> el Super Admin puede activar/desactivar funcionalidades (Bot Studio, Difusión Masiva, IA, etc.) por separado en cada uno desde el panel de admin.</p>
          </div>
        </CardContent>
      </Card>

      {/* New workspace dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo WhatsApp</DialogTitle>
            <DialogDescription>Le ponés un nombre para identificarlo (ej: "WA Ventas", "WA Soporte").</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitNew} className="space-y-3">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nombre del WhatsApp" required maxLength={50} autoFocus data-testid="ws-new-name" />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button type="submit" disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="ws-new-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Crear
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Renombrar WhatsApp</DialogTitle></DialogHeader>
          <Input value={editingName} onChange={(e) => setEditingName(e.target.value)} maxLength={50} autoFocus data-testid="ws-edit-name" />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={submitEdit} disabled={busy} data-testid="ws-edit-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
